import React, { useEffect, useState } from 'react';
import { Alert, Button, Col, Collapse, Flex, InputNumber, Radio, Row, Typography } from 'antd';
import { AimOutlined, CheckCircleFilled, DeleteOutlined, PlusOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import GreenLightButton from '../../../components/UI/Buttons/GreenLightButton';
import LspivService from '../../../services/LspivService';
import FrameCanvas, { Line, Marker } from './FrameCanvas';

const { Title, Text } = Typography;

const SCALE_COLOR = '#FA8C16';
const SECTION_COLOR = '#1677FF';
const BANK_DISTANCES = ['d12', 'd23', 'd34', 'd41', 'd13', 'd24'];

const pointOrder = (mode) => (mode === 'drone' ? ['S1', 'S2', 'L', 'R'] : ['S1', 'S2', 'S3', 'S4', 'L', 'R']);

const MarkupStep = ({ jobId, job, onRefresh, onNext }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState('drone');
    const [points, setPoints] = useState({});
    const [active, setActive] = useState(null);
    const [distance, setDistance] = useState(null);
    const [distances, setDistances] = useState({});
    const [depthMode, setDepthMode] = useState('mean');
    const [meanDepth, setMeanDepth] = useState(null);
    const [profile, setProfile] = useState([null, null, null]);
    const [alpha, setAlpha] = useState(0.85);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const frame = job?.status?.first_frame;
    const order = pointOrder(mode);
    const current = active || order.find((k) => !points[k]) || null;

    // Повторный расчёт: подставляем прошлую разметку.
    useEffect(() => {
        LspivService.getRunParams(jobId).then((p) => {
            const cal = p.calibration;
            const restored = { L: [p.section.xl, p.section.yl], R: [p.section.xr, p.section.yr] };
            cal.points.forEach((pt, i) => { restored[`S${i + 1}`] = pt; });
            setMode(cal.mode);
            setPoints(restored);
            setDistance(cal.distance);
            setDistances(cal.distances || {});
            if (p.depths.length > 1) {
                setDepthMode('profile');
                setProfile(p.depths);
            } else {
                setMeanDepth(p.depths[0]);
            }
            setAlpha(p.alpha);
        }).catch(() => {});
    }, [jobId]);

    const changeMode = (value) => {
        setMode(value);
        setActive(null);
        setPoints((p) => ({ L: p.L, R: p.R })); // точки масштаба у режимов разные, створ общий
    };

    const pick = (x, y) => {
        if (!current) return;
        const next = { ...points, [current]: [x, y] };
        setPoints(next);
        setActive(null);
    };

    const depths = depthMode === 'mean' ? [meanDepth] : profile;
    const depthsValid = depths.length > 0 && depths.every((d) => d !== null && d >= 0) && Math.max(...depths) > 0;
    const scaleValid = mode === 'drone'
        ? distance > 0
        : BANK_DISTANCES.every((k) => distances[k] > 0);
    const pointsValid = order.every((k) => points[k]);
    const canSubmit = pointsValid && scaleValid && depthsValid && alpha > 0;

    const submit = async () => {
        setSubmitting(true);
        setError(null);
        const scaleKeys = order.filter((k) => k.startsWith('S'));
        try {
            await LspivService.run(jobId, {
                calibration: {
                    mode,
                    points: scaleKeys.map((k) => points[k]),
                    distance: mode === 'drone' ? distance : null,
                    distances: mode === 'bank' ? distances : null,
                },
                section: { xl: points.L[0], yl: points.L[1], xr: points.R[0], yr: points.R[1] },
                depths,
                alpha,
            });
            await onRefresh();
            onNext();
        } catch (e) {
            setError(e?.response?.data?.detail || t('lspiv.markup.runError'));
        } finally {
            setSubmitting(false);
        }
    };

    const label = (k) => (k === 'L' ? t('lspiv.markup.leftShort') : k === 'R' ? t('lspiv.markup.rightShort') : k.slice(1));

    const chip = (k) => (
        <Button
            key={k}
            size="small"
            type={current === k ? 'primary' : 'default'}
            icon={points[k] ? <CheckCircleFilled style={{ color: current === k ? '#fff' : '#52c41a' }} /> : null}
            onClick={() => setActive(k)}
        >
            {label(k)}
        </Button>
    );

    if (!frame) {
        return <Alert type="warning" showIcon message={t('lspiv.markup.noFrames')} />;
    }

    const scaleKeys = order.filter((k) => k.startsWith('S'));

    return (
        <Flex vertical gap={16}>
            <Title level={4} style={{ margin: 0 }}>
                <AimOutlined /> {t('lspiv.markup.title')}
            </Title>

            <Flex gap={12} align="center" wrap="wrap">
                <Text strong>{t('lspiv.markup.howFilmed')}</Text>
                <Radio.Group value={mode} onChange={(e) => changeMode(e.target.value)} optionType="button" buttonStyle="solid">
                    <Radio.Button value="drone">{t('lspiv.markup.modeDrone')}</Radio.Button>
                    <Radio.Button value="bank">{t('lspiv.markup.modeBank')}</Radio.Button>
                </Radio.Group>
            </Flex>

            <Row gutter={[24, 16]}>
                <Col xs={24} lg={15}>
                    <Alert
                        style={{ marginBottom: 12 }}
                        type={current ? 'info' : 'success'}
                        showIcon
                        message={current
                            ? t(`lspiv.markup.hint.${current.startsWith('S') ? `${mode}.${current}` : current}`)
                            : t('lspiv.markup.hint.done')}
                    />
                    <FrameCanvas src={LspivService.frameUrl(jobId, frame)} onPick={current ? pick : null}>
                        {({ unit }) => (
                            <>
                                {mode === 'drone' && points.S1 && points.S2 && (
                                    <Line a={points.S1} b={points.S2} unit={unit} color={SCALE_COLOR} dashed />
                                )}
                                {mode === 'bank' && scaleKeys.map((k, i) => {
                                    const n = scaleKeys[(i + 1) % scaleKeys.length];
                                    return points[k] && points[n]
                                        ? <Line key={k} a={points[k]} b={points[n]} unit={unit} color={SCALE_COLOR} dashed />
                                        : null;
                                })}
                                {points.L && points.R && (
                                    <Line a={points.L} b={points.R} unit={unit} color={SECTION_COLOR} />
                                )}
                                {order.filter((k) => points[k]).map((k) => (
                                    <Marker
                                        key={k}
                                        x={points[k][0]}
                                        y={points[k][1]}
                                        unit={unit}
                                        color={k.startsWith('S') ? SCALE_COLOR : SECTION_COLOR}
                                        label={label(k)}
                                        active={current === k}
                                    />
                                ))}
                            </>
                        )}
                    </FrameCanvas>
                    <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>{t('lspiv.markup.replaceHint')}</Text>
                </Col>

                <Col xs={24} lg={9}>
                    <Flex vertical gap={20}>
                        <Flex vertical gap={8}>
                            <Text strong><span style={{ color: SCALE_COLOR }}>●</span> {t('lspiv.markup.scaleTitle')}</Text>
                            <Text type="secondary">{t(`lspiv.markup.scaleHint.${mode}`)}</Text>
                            <Flex gap={6} wrap="wrap">{scaleKeys.map(chip)}</Flex>
                            {mode === 'drone' ? (
                                <InputNumber
                                    value={distance}
                                    onChange={setDistance}
                                    min={0.1}
                                    step={0.5}
                                    addonBefore={t('lspiv.markup.distance')}
                                    addonAfter={t('lspiv.markup.meters')}
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <Row gutter={[8, 8]}>
                                    {BANK_DISTANCES.map((k) => (
                                        <Col span={12} key={k}>
                                            <InputNumber
                                                value={distances[k]}
                                                onChange={(v) => setDistances((d) => ({ ...d, [k]: v }))}
                                                min={0.1}
                                                addonBefore={`${k[1]}–${k[2]}`}
                                                addonAfter={t('lspiv.markup.meters')}
                                                style={{ width: '100%' }}
                                            />
                                        </Col>
                                    ))}
                                </Row>
                            )}
                        </Flex>

                        <Flex vertical gap={8}>
                            <Text strong><span style={{ color: SECTION_COLOR }}>●</span> {t('lspiv.markup.sectionTitle')}</Text>
                            <Text type="secondary">{t('lspiv.markup.sectionHint')}</Text>
                            <Flex gap={6}>{['L', 'R'].map(chip)}</Flex>
                        </Flex>

                        <Flex vertical gap={8}>
                            <Text strong>{t('lspiv.markup.depthTitle')}</Text>
                            <Radio.Group value={depthMode} onChange={(e) => setDepthMode(e.target.value)} size="small">
                                <Radio value="mean">{t('lspiv.markup.depthMean')}</Radio>
                                <Radio value="profile">{t('lspiv.markup.depthProfile')}</Radio>
                            </Radio.Group>
                            {depthMode === 'mean' ? (
                                <InputNumber
                                    value={meanDepth}
                                    onChange={setMeanDepth}
                                    min={0.01}
                                    step={0.1}
                                    addonBefore={t('lspiv.markup.depth')}
                                    addonAfter={t('lspiv.markup.meters')}
                                    style={{ width: '100%' }}
                                />
                            ) : (
                                <>
                                    <Text type="secondary">{t('lspiv.markup.depthProfileHint')}</Text>
                                    {profile.map((d, i) => (
                                        <Flex key={i} gap={6}>
                                            <InputNumber
                                                value={d}
                                                onChange={(v) => setProfile((p) => p.map((x, j) => (j === i ? v : x)))}
                                                min={0}
                                                step={0.1}
                                                addonBefore={`${i + 1}`}
                                                addonAfter={t('lspiv.markup.meters')}
                                                style={{ flex: 1 }}
                                            />
                                            <Button
                                                icon={<DeleteOutlined />}
                                                disabled={profile.length <= 2}
                                                onClick={() => setProfile((p) => p.filter((_, j) => j !== i))}
                                            />
                                        </Flex>
                                    ))}
                                    <Button icon={<PlusOutlined />} onClick={() => setProfile((p) => [...p, null])}>
                                        {t('lspiv.markup.addDepth')}
                                    </Button>
                                </>
                            )}
                        </Flex>

                        <Collapse
                            size="small"
                            items={[{
                                key: 'advanced',
                                label: t('lspiv.markup.advanced'),
                                children: (
                                    <Flex vertical gap={6}>
                                        <InputNumber
                                            value={alpha}
                                            onChange={setAlpha}
                                            min={0.5}
                                            max={1}
                                            step={0.05}
                                            addonBefore="α"
                                            style={{ width: '100%' }}
                                        />
                                        <Text type="secondary">{t('lspiv.markup.alphaHint')}</Text>
                                    </Flex>
                                ),
                            }]}
                        />

                        {error && <Alert type="error" showIcon message={error} />}

                        <GreenLightButton onClick={submit} loading={submitting} disabled={!canSubmit} style={{ height: 44 }}>
                            <PlayCircleOutlined /> {t('lspiv.markup.compute')}
                        </GreenLightButton>
                        {!canSubmit && <Text type="secondary">{t('lspiv.markup.fillAll')}</Text>}
                    </Flex>
                </Col>
            </Row>
        </Flex>
    );
};

export default MarkupStep;
